import { Cross2Icon } from '@radix-ui/react-icons'
import {
  Button,
  CheckboxCards,
  Container,
  Flex,
  IconButton,
  Separator,
  Text,
  TextField,
} from '@radix-ui/themes'
import { Form } from 'radix-ui'
import { PinnedResultsList } from './pinned-results-list'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SearchServiceAPI } from '@API/data-services/search-service'
import {
  SearchCollection,
  SearchOverride,
  SearchResult,
  SearchResultPage,
} from '@Generated/back-office-client/src'
import { RootState } from '@AppState/redux-store'
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@tanstack/react-query'

export type UpsertSearchRuleProps = {
  existingOverride?: SearchOverride
  onClose: () => void
  onSubmit: () => void
  currentCollection: SearchCollection
}

export const UpsertSearchRule = ({
  existingOverride,
  onClose,
  currentCollection,
  onSubmit,
}: UpsertSearchRuleProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const pageSize = 10

  const [searchResultPage, setSearchResultPage] = useState(1)
  const [pinnedDocs, setPinnedDocs] = useState<SearchResult[]>(existingOverride?.documents || [])
  const [currentResults, setCurrentResults] = useState<SearchResultPage>()
  const { activeCustomerId } = useSelector((state: RootState) => state.users)

  const [searchTerm, setSearchTerm] = useState<string>('')

  const { data: queryResults, isLoading } = useQuery({
    queryKey: [`${activeCustomerId}-${searchTerm}-${searchResultPage}`],
    queryFn: async () => {
      return SearchServiceAPI.searchDocuments(
        currentCollection.hostname,
        'name',
        searchTerm,
        searchResultPage,
        pageSize,
      )
    },
    enabled: !!searchTerm,
  })

  // Save off search in a different state so that a new search doesn't wipe out the current results
  useEffect(() => {
    if (queryResults) {
      if (searchResultPage === 1) {
        setCurrentResults(queryResults)
      } else if (currentResults) {
        setCurrentResults({
          ...currentResults,
          results: [...currentResults.results, ...queryResults.results],
        })
      }
    }
    // intentionally leave currentRsults out here to avoid infinite rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResults, searchResultPage])

  const {
    mutate: createSearchOverride,
    isSuccess,
    isPending,
  } = useMutation({
    mutationFn: (name: string) => {
      return SearchServiceAPI.upsertSearchOverride({
        documentIds: pinnedDocs.map((d) => d.id),
        hostname: currentCollection.hostname,
        name,
      })
    },
  })

  useEffect(() => {
    if (isSuccess) {
      onSubmit()
    }
  }, [isSuccess, onSubmit])

  useEffect(() => {
    if (!currentResults) {
      return
    }
    if (!containerRef.current || !sentinelRef.current) {
      return
    }

    // Clean up any existing observer
    observerRef.current?.disconnect()

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        const hasMorePages = currentResults.totalResults > searchResultPage * pageSize
        if (entry.isIntersecting && hasMorePages && !isLoading) {
          setSearchResultPage(searchResultPage + 1)
        }
      },
      {
        root: containerRef.current, // <- observe within the scroll container
        rootMargin: '200px', // prefetch before reaching the true bottom
        threshold: 0,
      },
    )

    observerRef.current.observe(sentinelRef.current)
    return () => observerRef.current?.disconnect()
  }, [currentResults, isLoading, searchResultPage])

  const results = useMemo(() => {
    if (!currentResults) {
      return
    }

    const pinnedIds = pinnedDocs.map((p) => p.id)
    const filteredResults = currentResults.results.filter((r) => !pinnedIds.includes(r.id))

    return filteredResults.map((q) => {
      const matchingTermStartIndex = q.name.indexOf(searchTerm)
      const name = (
        <Text>
          {q.name.substring(0, matchingTermStartIndex)}
          <mark>
            {q.name.substring(matchingTermStartIndex, matchingTermStartIndex + searchTerm.length)}
          </mark>
          {q.name.substring(matchingTermStartIndex + searchTerm.length, q.name.length)}
        </Text>
      )

      return (
        <CheckboxCards.Item key={q.id} value={q.id}>
          <Flex gap="5">
            <Flex direction="column" align="end">
              <Text>name</Text>
              <Text>result_type</Text>
              <Text>id</Text>
            </Flex>
            <Separator orientation="vertical" size="3" />
            <Flex direction="column">
              <Text>{name}</Text>
              <Text>{q.resultType}</Text>
              <Text>{q.id}</Text>
            </Flex>
          </Flex>
        </CheckboxCards.Item>
      )
    })
  }, [currentResults, pinnedDocs, searchTerm])

  return (
    <Container className={existingOverride ? 'edit-rule-container' : 'create-rule-form-container'}>
      <Form.Root
        onSubmit={(e) => {
          e.preventDefault()
          const formData = new FormData(e.currentTarget).entries()
          const data = Object.fromEntries(formData)

          createSearchOverride(data.overrideName as string)
        }}>
        <Flex direction="column" className="create-rule-form-container">
          {existingOverride ? null : (
            <Flex justify="between">
              <Text>New Override</Text>
              <Flex align="center">
                <IconButton variant="ghost" aria-label="Close" onClick={onClose}>
                  <Cross2Icon height="20" width="20" style={{ color: 'black' }} />
                </IconButton>
              </Flex>
            </Flex>
          )}

          <Form.Field name="overrideName">
            <Flex justify="between">
              <Form.Label>Override Name</Form.Label>
              <Form.Message className="FormMessage" match="valueMissing">
                Override is required
              </Form.Message>
            </Flex>

            <Form.Control asChild required>
              <TextField.Root placeholder="Name" defaultValue={existingOverride?.name} />
            </Form.Control>
          </Form.Field>

          <PinnedResultsList
            pinnedResults={pinnedDocs}
            setPinnedOrder={setPinnedDocs}
            onResultRemoved={(result) => {
              if (currentResults) {
                const alreadyInResults = currentResults.results.find((r) => r.id === result.id)
                if (!alreadyInResults) {
                  setCurrentResults({
                    ...currentResults,
                    results: [...currentResults.results, result],
                  })
                }
              }
            }}
          />

          <Separator size="4" />

          <Form.Field name="search-documents">
            <Flex justify="between">
              <Form.Label>Search Documents</Form.Label>
            </Flex>

            <Form.Control asChild>
              <TextField.Root
                value={searchTerm}
                placeholder="e.g., Powerball, Megamillions"
                onChange={(e) => {
                  const searchTerm = e.target.value
                  setSearchTerm(searchTerm)
                  setSearchResultPage(1)
                  if (!searchTerm) {
                    setCurrentResults(undefined)
                  }
                }}
              />
            </Form.Control>
          </Form.Field>

          <Flex direction="column" ref={containerRef} className="search-document-container">
            <CheckboxCards.Root
              key={currentResults?.results.length}
              columns={{ initial: '1', sm: '2' }}
              onValueChange={(checkedResults) => {
                const matches = currentResults!.results?.filter((r) =>
                  checkedResults.includes(r.id),
                )
                const newResults = currentResults!.results?.filter(
                  (r) => !checkedResults.includes(r.id),
                )
                setPinnedDocs([...pinnedDocs, ...matches])
                setCurrentResults({ ...currentResults!, results: newResults })
              }}>
              {results}
            </CheckboxCards.Root>
            <div ref={sentinelRef} style={{ height: 1 }} />
          </Flex>

          <Form.Submit asChild className="submit-rule">
            <Button
              style={{ cursor: 'pointer' }}
              loading={isPending}
              disabled={isPending || !pinnedDocs.length}>
              Save
            </Button>
          </Form.Submit>
        </Flex>
      </Form.Root>
    </Container>
  )
}
